import { getExpressBaseUrl } from '@/composables/useApi'
import { getEnv } from '@/composables/useEnv'
import { closeIntercom } from '@/composables/useIntercom'
import { IUserFromToken } from '@lifk/types'
import { useStorage } from '@vueuse/core'
import axios from 'axios'
import * as jose from 'jose'
import { defineStore } from 'pinia'

/*
  handles login tokens
*/

interface IUserStoreState {
  token: string | null
}

// whole store
export const useUserStore = defineStore({
  id: 'user',
  state: () =>
    ({
      token: useStorage('auth.token', null) // stores and reads from local storage
    }) as unknown as IUserStoreState,
  getters: {
    user: (state): IUserFromToken | null => {
      if (!state.token) {
        return null
      }
      let data: unknown = null
      try {
        data = jose.decodeJwt(state.token)
      } catch (_) {
        data = null
      }
      return data as IUserFromToken
    },
    userIsGuest(): boolean {
      return this.user !== null && this.user.role === 'guest'
    },
    userIsLoggedIn(): boolean {
      return this.user !== null && this.user.role !== 'guest'
    },
    userIsAnonymousGuest(): boolean {
      if (!this.userIsGuest || !this.user) return false
      return (
        this.user.isAnonymous ?? !(this.user.firstName || this.user.lastName)
      )
    }
  },
  actions: {
    async initializeToken() {
      if (!this.token) {
        try {
          const { token } = (
            await axios.get(`${getExpressBaseUrl()}/api/auth/guest`, {
              // Cannot use the useAPI composable here
              // Very important! Add the version header to this request
              headers: {
                version: getEnv('VITE_APP_COMMIT_SHA') ?? 'review'
              }
            })
          ).data
          this.token = token
        } catch (error) {
          console.warn('Failed to provision guest token', error)
        }
      }
    },
    setToken(value: string | null) {
      this.token = value
    },
    logout() {
      this.token = null
      // This is important! Clear all intercom data when loggin out
      closeIntercom()
    }
  }
})
